import { useTranslations } from '../../i18n';
import { theme } from '../../lib/theme';
import { Bell, Clock, Play, Check } from 'lucide-react';
import { ReminderFormData } from '../../types/reminders';
import { RichTextEditor } from '../ui/RichTextEditor';
import { usePreferences } from '../../contexts/PreferencesContext';
import { formatTime } from '../../lib/timeUtils';
import { DateInput } from '../ui/DateInput';
import { TimeInput } from '../ui/TimeInput';
import { FormLabel } from '../ui/FormLabel';

interface ReminderFormSectionProps {
    data: ReminderFormData | null;
    onChange: (data: ReminderFormData | null) => void;
    isReadOnly?: boolean;
    showNameField?: boolean;
    isStandalone?: boolean;
    onDelete?: () => void;
    isAdminView?: boolean;
    onSimulate?: () => void;
    reminderId?: string;
    onMarkSeen?: () => Promise<void>;
}

export function ReminderFormSection({
    data,
    onChange,
    isReadOnly = false,
    showNameField = true,
    isStandalone = false,
    onDelete,
    isAdminView = false,
    onSimulate,
    reminderId,
    onMarkSeen
}: ReminderFormSectionProps) {
    const { t, language } = useTranslations();
    const { preferences } = usePreferences();

    const handleToggle = () => {
        if (isReadOnly) return;
        if (data) {
            onChange(null);
        } else {
            // Default new reminder
            const nextWeek = new Date();
            nextWeek.setDate(nextWeek.getDate() + 7);
            
            onChange({
                name: '',
                description: '',
                remind_date: nextWeek.toISOString().split('T')[0],
                remind_time: '12:00',
                extra_reminders: false,
                repeat_yearly: false,
                is_active: true
            });
        }
    };

    const updateField = (field: keyof ReminderFormData, value: any) => {
        if (isReadOnly || !data) return;
        onChange({ ...data, [field]: value });
    };

    return (
        <div className="space-y-4">
            {!isStandalone && (
                <div className="flex items-center justify-between mb-2 px-1">
                    <div className="flex items-center gap-2">
                        <span className={theme.typography.sectionTitle}>
                            {t.reminders.reminder}
                        </span>
                    </div>
                    {!isReadOnly && (
                        <button
                            type="button"
                            role="switch"
                            aria-checked={!!data}
                            onClick={handleToggle}
                            className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${data ? 'bg-blue-600' : 'bg-gray-200 dark:bg-slate-700'}`}
                        >
                            <span
                                aria-hidden="true"
                                className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${data ? 'translate-x-5' : 'translate-x-0'}`}
                            />
                        </button>
                    )}
                </div>
            )}

            {data && (
                <div className={isStandalone ? "space-y-6 animate-in fade-in slide-in-from-top-2 duration-300" : "bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm p-4 space-y-6 animate-in fade-in slide-in-from-top-2 duration-300"}>
                    {showNameField && (
                        <>
                            <div>
                                <FormLabel className={theme.typography.sectionLabel}>
                                    {t.reminders.nameLabel} {isStandalone ? '*' : ''}
                                </FormLabel>
                                <input
                                    type="text"
                                    value={data.name}
                                    disabled={isReadOnly}
                                    onChange={(e) => updateField('name', e.target.value)}
                                    className={`w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-bold text-gray-900 dark:text-white placeholder:text-gray-300 dark:placeholder:text-white/20 text-[15px] ${!isStandalone ? 'bg-gray-50/50 dark:bg-slate-900/60' : ''}`}
                                    placeholder={t.reminders.namePlaceholder}
                                />
                            </div>

                            <div>
                                <FormLabel className={theme.typography.sectionLabel}>
                                    {t.reminders.descriptionLabel}
                                </FormLabel>
                                <RichTextEditor
                                    content={data.description}
                                    onChange={(c) => updateField('description', c)}
                                    readOnly={isReadOnly}
                                    placeholder={t.reminders.descriptionPlaceholder}
                                    minHeight="100px"
                                />
                            </div>
                        </>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <DateInput
                            value={data.remind_date}
                            onChange={(v) => updateField('remind_date', v)}
                            disabled={isReadOnly}
                            language={language}
                            className={!isStandalone ? 'bg-gray-50/50 dark:bg-slate-900/60 rounded-xl overflow-hidden' : ''}
                        />
                        <div>
                            {preferences?.time_format === '12h' ? (
                                <div className="flex items-center gap-2">
                                    <div className="relative flex-1">
                                        <select
                                            value={(() => {
                                                const [h] = (data.remind_time || '12:00').split(':').map(Number);
                                                return h % 12 || 12;
                                            })()}
                                            onChange={(e) => {
                                                const h12 = parseInt(e.target.value);
                                                const [, m] = (data.remind_time || '12:00').split(':');
                                                const h24 = (data.remind_time || '12:00').split(':').map(Number)[0];
                                                const isPM = h24 >= 12;
                                                let finalH24 = h12;
                                                if (isPM) {
                                                    finalH24 = h12 === 12 ? 12 : h12 + 12;
                                                } else {
                                                    finalH24 = h12 === 12 ? 0 : h12;
                                                }
                                                updateField('remind_time', `${finalH24.toString().padStart(2, '0')}:${m}`);
                                            }}
                                            disabled={isReadOnly}
                                            className={`w-full px-3 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 font-bold text-gray-900 dark:text-white text-[15px] appearance-none text-center ${!isStandalone ? 'bg-gray-50/50 dark:bg-slate-900/60' : ''}`}
                                        >
                                            {Array.from({ length: 12 }, (_, i) => i + 1).map(h => (
                                                <option key={h} value={h}>{h.toString().padStart(2, '0')}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <span className="text-gray-400 font-bold">:</span>
                                    <div className="relative flex-1">
                                        <select
                                            value={(data.remind_time || '12:00').split(':')[1]}
                                            onChange={(e) => {
                                                const newM = e.target.value;
                                                const [h] = (data.remind_time || '12:00').split(':');
                                                updateField('remind_time', `${h}:${newM}`);
                                            }}
                                            disabled={isReadOnly}
                                            className={`w-full px-3 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 font-bold text-gray-900 dark:text-white text-[15px] appearance-none text-center ${!isStandalone ? 'bg-gray-50/50 dark:bg-slate-900/60' : ''}`}
                                        >
                                            {Array.from({ length: 60 }, (_, i) => i).map(m => (
                                                <option key={m} value={m.toString().padStart(2, '0')}>{m.toString().padStart(2, '0')}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="relative w-20">
                                        <select
                                            value={parseInt((data.remind_time || '12:00').split(':')[0]) >= 12 ? 'PM' : 'AM'}
                                            onChange={(e) => {
                                                const newPeriod = e.target.value;
                                                const [h24, m] = (data.remind_time || '12:00').split(':').map(Number);
                                                const h12 = h24 % 12 || 12;
                                                let finalH24 = h12;
                                                if (newPeriod === 'PM') {
                                                    finalH24 = h12 === 12 ? 12 : h12 + 12;
                                                } else {
                                                    finalH24 = h12 === 12 ? 0 : h12;
                                                }
                                                updateField('remind_time', `${finalH24.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
                                            }}
                                            disabled={isReadOnly}
                                            className={`w-full px-2 py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-500/20 rounded-xl focus:ring-2 focus:ring-blue-500 font-black text-blue-600 dark:text-blue-400 text-[13px] appearance-none text-center`}
                                        >
                                            <option value="AM">AM</option>
                                            <option value="PM">PM</option>
                                        </select>
                                    </div>
                                </div>
                            ) : (
                                <TimeInput
                                    value={data.remind_time || '12:00'}
                                    onChange={(v: string) => updateField('remind_time', v)}
                                    disabled={isReadOnly}
                                    preferences={preferences}
                                    className={!isStandalone ? 'bg-gray-50/50 dark:bg-slate-900/60 rounded-xl' : ''}
                                />
                            )}
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className={`bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm divide-y divide-gray-100 dark:divide-white/5 ${!isStandalone ? 'p-0 shadow-none border-gray-100 dark:border-white/5 bg-transparent dark:bg-transparent' : ''}`}>
                            {/* Yearly Repeat */}
                            <div className={`flex items-center justify-between p-4 transition-colors hover:bg-gray-50 dark:hover:bg-white/5 ${!isStandalone ? 'px-1 py-3.5' : ''}`}>
                                <div className="flex flex-col pr-8">
                                    <span className="text-[15px] font-semibold text-gray-900 dark:text-white">
                                        {t.reminders.repeatYearly}
                                    </span>
                                    <p className="text-[12px] text-gray-500 dark:text-gray-400 leading-tight mt-0.5">
                                        {(t.reminders as any).repeatYearlyDesc || 'Šis atgādinājums notiks automātiski katru gadu tajā pašā datumā/mēnesī/dienā/laikā līdz tiks izslēgts'}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={data.repeat_yearly}
                                    onClick={() => updateField('repeat_yearly', !data.repeat_yearly)}
                                    disabled={isReadOnly}
                                    className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${data.repeat_yearly ? 'bg-blue-600 dark:bg-blue-500' : 'bg-gray-200 dark:bg-slate-700'} ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <span
                                        aria-hidden="true"
                                        className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${data.repeat_yearly ? 'translate-x-5' : 'translate-x-0'}`}
                                    />
                                </button>
                            </div>

                            {/* Extra Reminders */}
                            <div className={`flex items-center justify-between p-4 transition-colors hover:bg-gray-50 dark:hover:bg-white/5 ${!isStandalone ? 'px-1 py-3.5' : ''}`}>
                                <div className="flex flex-col pr-8">
                                    <span className="text-[15px] font-semibold text-gray-900 dark:text-white">
                                        {t.reminders.extraReminders}
                                    </span>
                                    <p className="text-[12px] text-gray-500 dark:text-gray-400 leading-tight mt-0.5">
                                        {t.reminders.extraRemindersDesc}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={data.extra_reminders}
                                    onClick={() => updateField('extra_reminders', !data.extra_reminders)}
                                    disabled={isReadOnly}
                                    className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${data.extra_reminders ? 'bg-blue-600 dark:bg-blue-500' : 'bg-gray-200 dark:bg-slate-700'} ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <span
                                        aria-hidden="true"
                                        className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${data.extra_reminders ? 'translate-x-5' : 'translate-x-0'}`}
                                    />
                                </button>
                            </div>

                             {/* Is Active */}
                             <div className={`flex items-center justify-between p-4 transition-colors hover:bg-gray-50 dark:hover:bg-white/5 ${!isStandalone ? 'px-1 py-3.5 border-b-0' : ''}`}>
                                <div className="flex flex-col pr-8">
                                    <span className="text-[15px] font-semibold text-gray-900 dark:text-white">
                                        {t.reminders.active}
                                    </span>
                                    <p className="text-[12px] text-gray-500 dark:text-gray-400 leading-tight mt-0.5">
                                        {t.reminders.activeDesc}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={data.is_active}
                                    onClick={() => updateField('is_active', !data.is_active)}
                                    disabled={isReadOnly}
                                    className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${data.is_active ? 'bg-blue-600 dark:bg-blue-500' : 'bg-gray-200 dark:bg-slate-700'} ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <span
                                        aria-hidden="true"
                                        className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${data.is_active ? 'translate-x-5' : 'translate-x-0'}`}
                                    />
                                </button>
                            </div>
                        </div>

                        {/* Summary of Next Reminder */}
                        {(() => {
                            const isFuture = data.remind_date ? new Date(`${data.remind_date}T${data.remind_time || '00:00'}`).getTime() >= Date.now() : false;
                            if (!data.is_active || !isFuture) return null;
                            
                            return (
                                <div className="p-4 bg-gray-50/50 dark:bg-slate-900/40 rounded-2xl border border-gray-100 dark:border-white/5">
                                    <div className="flex flex-col gap-1">
                                        <span className="text-[11px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest leading-none">
                                            {(t.reminders as any).nextReminder || 'Next reminder'}
                                        </span>
                                        <div className="items-center gap-2 mt-2 flex">
                                            <div className="p-2 bg-blue-100 dark:bg-blue-500/10 rounded-lg text-blue-600 dark:text-blue-400">
                                                <Clock size={16} />
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-[15px] font-bold text-gray-900 dark:text-white leading-tight">
                                                    {(() => {
                                                        if (!data.remind_date) return '-';
                                                        const d = new Date(data.remind_date);
                                                        const dateStr = d.toLocaleDateString(language || 'en', { day: '2-digit', month: 'short', year: 'numeric' });
                                                        const timeStr = data.remind_time ? formatTime(data.remind_time, preferences) : '';
                                                        return `${dateStr}, ${timeStr}`;
                                                    })()}
                                                </span>
                                                <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 leading-tight">
                                                    {preferences?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Triggered Status Banner with Check Action */}
                        {data.action_required && !isReadOnly && (
                            <div className="mt-2 p-4 bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/20 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-4 animate-remind-pulse shadow-sm shadow-green-500/10">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-green-500 rounded-full animate-pulse shadow-[0_0_12px_rgba(34,197,94,0.6)]">
                                        <Bell className="text-white" size={16} fill="currentColor" />
                                    </div>
                                    <div>
                                        <h5 className="text-[15px] font-black text-green-700 dark:text-green-300 leading-tight">
                                            {(t.reminders as any).triggeredBannerTitle || 'Reminder Triggered!'}
                                        </h5>
                                        <p className="text-[12px] font-bold text-green-600/70 dark:text-green-400/70 tracking-tight">
                                            {(t.reminders as any).triggeredBannerDesc || 'Currently active on dashboard.'}
                                        </p>
                                    </div>
                                </div>
                                
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onMarkSeen?.();
                                    }}
                                    className="w-full sm:w-auto flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-2.5 rounded-xl font-bold transition-all active:scale-95 shadow-md shadow-green-500/20"
                                >
                                    <Check size={18} strokeWidth={3} />
                                    <span>{t.common.done || 'Done'}</span>
                                </button>
                            </div>
                        )}

                        {/* Simulation Button (Only for Admins) */}
                        {isAdminView && reminderId && onSimulate && (
                            <div className="mt-2">
                                <button
                                    type="button"
                                    onClick={onSimulate}
                                    className="w-full py-3 bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-xl text-[14px] font-semibold hover:bg-blue-500/20 transition-all flex items-center justify-center gap-2 border border-blue-500/20 shadow-sm"
                                >
                                    <Play size={16} fill="currentColor" />
                                    {t.reminders.simulateReminder}
                                </button>
                            </div>
                        )}

                        {/* Additional Options Section (Delete - Only for Standalone) */}
                        {isStandalone && !isReadOnly && onDelete && (
                            <div className="space-y-4 pt-2">
                                <div className="px-1">
                                    <span className={theme.typography.sectionTitle}>{t.common.additionalOptions}</span>
                                </div>
                                <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm">
                                    <button
                                        type="button"
                                        onClick={onDelete}
                                        className="w-full text-left p-4 text-base font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                                    >
                                        {t.reminders.deleteReminder}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {!data && !isReadOnly && !isStandalone && (
                <div className="bg-gray-50/50 dark:bg-slate-900/20 border border-dashed border-gray-200 dark:border-white/10 rounded-2xl p-4 flex flex-col items-center justify-center text-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-slate-800 flex items-center justify-center text-gray-400">
                        <Bell size={20} />
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                            {t.reminders.noReminders}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
